/**
 * Private medical records (PMR) + 4142 for the Supporting evidence chapter.
 *
 * These screens only display if "Medical records from other healthcare
 * providers" is checked on /medical-records-types; they follow the VA medical
 * records loop when both record types are selected (enforced by page order
 * in config/form.jsx).
 *
 * arrayBuilderPages requires the order intro, summary, then item pages.
 * Item data shape: see `providerFacilityItemSchema` in schema/index.js.
 *
 * Note: the current-flow /private-medical-records-release URL (wizard
 * pattern) is deprecated and intentionally not scaffolded.
 */
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';

import {
  hasPrivateMedicalRecords,
  navForwardFromSummary,
  onNavBackFirstItemPage,
  onNavForwardIntoLoop,
  wantsVaToRequestPrivateRecords,
  willUploadPrivateRecords,
} from './utils';
import { isBDD } from '../../utils/bdd';
import { privateMedicalRecordsOptions } from './privateMedicalRecords/options';
import introPage from './privateMedicalRecords/intro';
import accessPage from './privateMedicalRecords/access';
import authorizeReleasePage from './privateMedicalRecords/authorizeRelease';
import providerDetailsPage from './privateMedicalRecords/providerDetails';
import providerTypePage from './privateMedicalRecords/providerType';
import providerConditionsPage from './privateMedicalRecords/providerConditions';
import providerDatePage from './privateMedicalRecords/providerDate';
import summaryPage from './privateMedicalRecords/summary';
import limitedConsentPage from './privateMedicalRecords/limitedConsent';
import {
  PMR_ACCESS_PATH,
  pmrUploadBypassedIntro,
} from './privateMedicalRecordsUploadPages';

/**
 * Prefix for the provider release loop's item pages. Per the Step 4 URL spec
 * in Figma (#157626) each item page is a flat slug with the index last, e.g.
 * supporting-evidence/other-providers-type/:index.
 */
const ITEM_PATH_PREFIX = 'supporting-evidence/other-providers';

/**
 * First item page path (with :index) — the card Edit target and the
 * add-mode entry point for the loop.
 */
const ITEM_PATH = `${ITEM_PATH_PREFIX}-name/:index`;

export const PRIVATE_MEDICAL_RECORDS_RELEASE_ITEM_PATH = ITEM_PATH;

const SUMMARY_PATH = 'supporting-evidence/other-providers-summary';

const AUTHORIZE_RELEASE_PATH =
  'supporting-evidence/other-providers-records-release-authorization';

/**
 * Loop descriptor for `getLoopEntryPath`: Continue from authorize-release
 * goes straight to the first item page (add mode) when no providers exist
 * yet, or the loop summary otherwise.
 */
const PRIVATE_MEDICAL_RECORDS_RELEASE_LOOP = {
  arrayPath: privateMedicalRecordsOptions.arrayPath,
  itemPath: ITEM_PATH,
  summaryPath: SUMMARY_PATH,
  // The summary page's arrayBuilderYesNoUI field; the summary's
  // `onNavForward` branches on it the same way platform's default does.
  hasItemsKey: 'view:hasProviderFacilities',
};

export const privateMedicalRecordsPages = {
  // PMR section intro. BDD claims answer Yes on /other-medical-records-bdd
  // and go straight to the access page.
  privateMedicalRecordsIntro: {
    path: 'supporting-evidence/other-providers-intro',
    title: 'Information about your records from other healthcare providers',
    depends: formData => hasPrivateMedicalRecords(formData) && !isBDD(formData),
    uiSchema: introPage.uiSchema,
    schema: introPage.schema,
  },
  ...arrayBuilderPages(privateMedicalRecordsOptions, pageBuilder => ({
    // Register PMR access page first so `introRoute` resolves here instead
    // of authorization. `onNavForward` is set below via
    // pmrUploadBypassedIntro.decisionPage.
    privateMedicalRecordsAccess: pageBuilder.introPage({
      title: 'Options for sharing records from other healthcare providers',
      path: PMR_ACCESS_PATH,
      depends: hasPrivateMedicalRecords,
      // "I'll upload my records myself" jumps straight into the upload loop
      // (first item page in add mode, or the loop summary if records already
      // exist); bypass the list & loop's intro page.
      ...pmrUploadBypassedIntro.decisionPage(willUploadPrivateRecords),
      // Intercepts Continue with a destructive confirmation modal when the
      // user switches options after already providing evidence under the
      // other one.
      CustomPage: accessPage.CustomPage,
      CustomPageReview: accessPage.CustomPageReview,
      uiSchema: accessPage.uiSchema,
      schema: accessPage.schema,
    }),
    // Also an `introPage` since `required` is a function (ArrayBuilder
    // README), but `access` above wins the `introRoute` lookup.
    privateMedicalRecordsAuthorizeRelease: pageBuilder.introPage({
      title: 'Authorize the release of records from other healthcare providers',
      path: AUTHORIZE_RELEASE_PATH,
      depends: wantsVaToRequestPrivateRecords,
      // Continue goes straight into the loop: the first item page (add mode)
      // if no providers exist yet, or the loop summary if they do.
      // By default, the optional-flow intro page (`required: false`) always
      // continues to the summary, even with zero items, and we are overriding this
      // default. This is a deliberate mix of behaviors of the required
      // and optional multi-page response patterns, not a platform limitation.
      onNavForward: onNavForwardIntoLoop(PRIVATE_MEDICAL_RECORDS_RELEASE_LOOP),
      CustomPage: authorizeReleasePage.CustomPage,
      CustomPageReview: authorizeReleasePage.CustomPageReview,
      uiSchema: authorizeReleasePage.uiSchema,
      schema: authorizeReleasePage.schema,
    }),
    // Deleting the last provider keeps the Veteran on this summary page,
    // with no cards present, to answer the Yes/No question. The design
    // system's optional-pattern docs describe redirecting to the intro
    // page instead; we intentionally diverge from that (see
    // https://va.ghe.com/software/vets-website/pull/48985).
    privateMedicalRecordsReleaseSummary: pageBuilder.summaryPage({
      title: 'Review details about other healthcare providers',
      path: SUMMARY_PATH,
      depends: wantsVaToRequestPrivateRecords,
      // Replaces platform's default navForward so the next page is worked
      // out with getPathAfterLoop, which correctly skips this loop's own
      // expanded item pages.
      onNavForward: navForwardFromSummary(PRIVATE_MEDICAL_RECORDS_RELEASE_LOOP),
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
    }),
    // A card's "Edit" link opens the first active item page, so keep the
    // provider details page first among the item pages.
    privateMedicalRecordsReleaseProvider: pageBuilder.itemPage({
      title: 'Details about other healthcare providers',
      path: ITEM_PATH,
      depends: wantsVaToRequestPrivateRecords,
      onNavBack: onNavBackFirstItemPage(
        PRIVATE_MEDICAL_RECORDS_RELEASE_LOOP,
        AUTHORIZE_RELEASE_PATH,
      ),
      uiSchema: providerDetailsPage.uiSchema,
      schema: providerDetailsPage.schema,
    }),
    // H3 is populated with the "Name of private provider or hospital"
    // entered on the provider page.
    privateMedicalRecordsReleaseType: pageBuilder.itemPage({
      title: 'Type of provider',
      path: `${ITEM_PATH_PREFIX}-type/:index`,
      depends: wantsVaToRequestPrivateRecords,
      uiSchema: providerTypePage.uiSchema,
      schema: providerTypePage.schema,
    }),
    privateMedicalRecordsReleaseConditions: pageBuilder.itemPage({
      title: 'Conditions treated at this provider',
      path: `${ITEM_PATH_PREFIX}-conditions/:index`,
      depends: wantsVaToRequestPrivateRecords,
      uiSchema: providerConditionsPage.uiSchema,
      schema: providerConditionsPage.schema,
    }),
    // Note from ticket: this is a "rogue" use of the date component; present
    // at midpoint review and fall back if platform objects.
    privateMedicalRecordsReleaseDate: pageBuilder.itemPage({
      title: 'Treatment dates at this provider',
      path: `${ITEM_PATH_PREFIX}-dates/:index`,
      depends: wantsVaToRequestPrivateRecords,
      uiSchema: providerDatePage.uiSchema,
      schema: providerDatePage.schema,
    }),
  })),
  privateMedicalRecordsLimitedConsent: {
    title: 'Limit what information we can request',
    // Slug per the Step 4 URL spec in Figma (#157626).
    path: 'supporting-evidence/other-providers-limited-consent',
    depends: wantsVaToRequestPrivateRecords,
    uiSchema: limitedConsentPage.uiSchema,
    schema: limitedConsentPage.schema,
  },
};

export default privateMedicalRecordsPages;
